// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { MatDialog } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, take } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { UsersService } from "src/app/modules/users/users.service";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { IUser } from "src/app/shared/models/user/user.interface";
import { User } from "src/app/shared/models/user/user.model";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { ThemeService } from "src/app/shared/services/theme.service";

@Component({
  selector: "app-user-detail",
  templateUrl: "./user-detail.page.html",
  styleUrls: ["./user-detail.page.scss"],
})
export class UserDetailComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  user: User = new User();
  userFullName: string;
  roles = [];
  companyId: string;
  displayedRole: any;
  userRoles: any[];
  subPropertiesFormly: any[] = [];

  // BEGIN - Edit user form
  form: FormGroup = new FormGroup({});
  formPassword: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  modelPassword: any = {};
  fieldsPassword: FormlyFieldConfig[] = [];
  optionsPassword: FormlyFormOptions = {};

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  formPasswordBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  deleteBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.delete"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "warn",
    spinnerColor: "warn",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-trash",
      inline: true,
    },
  };
  // END - Edit user form

  detailCardIsLoading = true;
  deleteCardIsLoading = true;
  paswordCardIsLoading = true;

  previousPermissions = {
    unlimited: false,
    administration: false,
    editCompanies: false,
    insertPaperDoc: false,
    properties: false,
    docSearch: false,
    timetable: false,
    companies: false,
    insertDigitalDoc: false,
    email: false,
    temporaries: false,
    users: false,
    delDoc: false,
    delAgreement: false,
    editDocData: false,
    propertiesUnlimited: false,
    alertLastDocs: false,
  };

  adminCompanyId: string;

  loggedUserObj: any;

  preventFirstChange = false;

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private usersService: UsersService,
    private router: Router,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private companiesService: CompaniesService,
    private apiService: ApiService // DO NOT MOVE since it's used for 'autocomplete' field type
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Subscribe to current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Load correct data into component basing on url params
    if (this.activatedRoute.params) {
      this.subscriptions.push(
        this.activatedRoute.params.subscribe(
          (res) => {
            if (res) {
              console.log("res", res);
              if (res.lang) {
                this.lang = res.lang;
              }
              if (res.adminCompanyId) {
                this.adminCompanyId = res.adminCompanyId;
              }
              this.handleRouteParamsChange();
            }
          },
          (err) => {
            // if we came here users with given userId do not exist
            this.router.navigate([this.lang, "errors", "404"]);
          }
        )
      );
    }
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init component
   */
  handleRouteParamsChange(): void {
    // Activate spinner
    this.detailCardIsLoading = true;
    this.paswordCardIsLoading = true;

    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("users.detail.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
        // this.loggedUserRole = this.authService.loggedUserRequestedRole$.getValue();
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );
    this.subscriptions.push(
      this.authService.loggedUserRequestedRoleObj$.subscribe(
        (res) => (this.loggedUserObj = res)
      )
    );

    // Get requested user data
    const userId = this.activatedRoute.snapshot.params.userId;
    // Admin/Operator: this param is useful to Admin and operator to manipulate
    // the user correct role, in relate to company
    const adminCompanyId = this.activatedRoute.snapshot.params.adminCompanyId;

    this.subscriptions.push(
      this.usersService.get(userId).subscribe(
        (res) => {
          if (res.status) {
            // Get user data
            this.user.setData(res.data);
            this.userFullName = `${this.user.get("firstName")} ${this.user.get(
              "lastName"
            )}`;
            this.userRoles = this.user.get("userRoles");

            // Stop loading delete form
            this.deleteCardIsLoading = false;

            // Prevent form fields to be rendered twice on url params change
            this.fields = [];

            // If logged user is admin/operator, he must see this user as admin/operator/tagger
            if (
              this.authService.loggedUserRequestedRoleType$.getValue() ===
                "admin" ||
              this.authService.loggedUserRequestedRoleType$.getValue() ===
                "operator"
            ) {
              // User cannot be either admin and operator, so return the first matched element
              // If company subuser, get correct role
              this.displayedRole = this.userRoles.find((obj: any) => {
                if (adminCompanyId) {
                  return (
                    obj.company &&
                    obj.company._id &&
                    obj.company._id.toString() === adminCompanyId
                  );
                } else {
                  const roleType =
                    obj.role && obj.role.type
                      ? obj.role.type.toString()
                      : obj.type;
                  return (
                    roleType === "admin" ||
                    roleType === "operator" ||
                    roleType === "tagger"
                  );
                }
              });
              if (!this.displayedRole) {
                //this.router.navigate([this.lang, 'errors', '404']);
                console.warn("err");
              }
            }

            // Same thing for master/collaborator
            if (
              this.authService.loggedUserRequestedRoleType$.getValue() ===
                "master" ||
              this.authService.loggedUserRequestedRoleType$.getValue() ===
                "collaborator"
            ) {
              this.displayedRole = this.userRoles.find((obj: any) => {
                const roleObj =
                  this.authService.loggedUserRequestedRoleObj$.getValue();
                const company =
                  roleObj && roleObj.company ? roleObj.company._id : undefined;
                const roleType =
                  obj.role && obj.role.type
                    ? obj.role.type.toString()
                    : obj.type;
                return (
                  (roleType === "master" || roleType === "collaborator") &&
                  obj.company &&
                  obj.company._id === company
                );
              });
              if (!this.displayedRole) {
                this.router.navigate([this.lang, "errors", "404"]);
              }
            }

            // Make form card visible by disabling his card-spinner
            this.detailCardIsLoading = false;
            this.paswordCardIsLoading = false;
            this.getCompany(userId, this.displayedRole);

            return;
          }

          // Something bad happened
          // redirect user to home
          this.router.navigate([this.lang, "app", "users", "list"]);
        },
        (err) => {
          // if we came here users with given userId do not exist
          this.router.navigate([this.lang, "errors", "404"]);
        }
      )
    );
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    const userData = this.user.getData();
    this.model = userData;
    this.model.generalPermission = this.displayedRole.generalPermission;

    // Set fields
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "firstName",
            templateOptions: {
              label: this.translateService.instant("users.fields.firstName"),
              placeholder: this.translateService.instant(
                "users.fields.firstName"
              ),
              required: true,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "lastName",
            templateOptions: {
              label: this.translateService.instant("users.fields.lastName"),
              placeholder: this.translateService.instant(
                "users.fields.lastName"
              ),
              required: true,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "taxCode",
            templateOptions: {
              label: this.translateService.instant("users.fields.taxCode"),
              placeholder: this.translateService.instant(
                "users.fields.taxCode"
              ),
              required: false,
              minLength: 0,
              maxLength: 20,
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "phone",
            templateOptions: {
              label: this.translateService.instant("users.fields.phone"),
              placeholder: this.translateService.instant("users.fields.phone"),
              required: false,
              minLength: 1,
              maxLength: 15,
              pattern: this.configService.phonePattern,
            },
          },
          {
            className: "col-12",
            type: "address",
            key: "address",
            templateOptions: {
              label: this.translateService.instant("users.fields.address"),
              placeholder: this.translateService.instant(
                "users.fields.address"
              ),
              required: false,
            },
          },
          {
            className: "section-label col-12",
            template:
              "<hr /><div><strong>" +
              this.translateService.instant(
                "users.fields.generalPermission.title"
              ) +
              "</strong></div>",
          },
          {
            key: "userRoleId",
            defaultValue: this.displayedRole._id,
          },
          {
            className: "col-12 checkbox-field",
            type: "checkbox",
            key: "generalPermission.unlimited",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.unlimited.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.unlimited.placeholder"
              ),
            },
            expressionProperties: {
              "templateOptions.required":
                "model.types && model.types.includes('manager')",
              "templateOptions.change": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                if (!this.preventFirstChange) {
                  this.preventFirstChange = true;
                } else {
                  const key = field.key as string;
                  const subKey = key.replace("generalPermission.", "");
                  if (
                    this.previousPermissions[subKey] !=
                    model.generalPermission[subKey]
                  ) {
                    this.previousPermissions[subKey] =
                      model.generalPermission[subKey];
                    this.managePermissionCheckbox(model, field, subKey);
                  }
                }
              },
            },
          },
          {
            className: "col-12 checkbox-field",
            type: "checkbox",
            key: "generalPermission.tagger",
            defaultValue: false,
            hideExpression:
              this.displayedRole.role.type !== "admin" &&
              this.displayedRole.role.type !== "operator" &&
              this.displayedRole.role.type !== "tagger",
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.tagger.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.tagger.placeholder"
              ),
            },
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.administration",
            defaultValue: false,
            hideExpression:
              this.displayedRole.role.type !== "admin" &&
              this.displayedRole.role.type !== "operator",
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.administration.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.administration.placeholder"
              ),
            },
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.editCompanies",
            defaultValue: false,
            hideExpression:
              this.displayedRole.role.type !== "admin" &&
              this.displayedRole.role.type !== "operator",
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.editCompanies.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.editCompanies.placeholder"
              ),
            },
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.companies",
            defaultValue: false,
            hideExpression:
              this.displayedRole.role.type === "admin" ||
              this.displayedRole.role.type === "operator" ||
              this.displayedRole.role.type === "tagger",
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.companies.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.companies.placeholder"
              ),
            },
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-12 checkbox-field",
            type: "checkbox",
            key: "generalPermission.properties",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.properties.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.properties.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.timetable",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.timetable.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.timetable.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.docSearch",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.docSearch.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.docSearch.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.insertPaperDoc",
            defaultValue: false,
            hideExpression:
              this.displayedRole.role.type !== "admin" &&
              this.displayedRole.role.type !== "operator",
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.insertPaperDoc.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.insertPaperDoc.placeholder"
              ),
            },
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.insertDigitalDoc",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.insertDigitalDoc.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.insertDigitalDoc.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.delDoc",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.delDoc.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.delDoc.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.delAgreement",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.delAgreement.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.delAgreement.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-12 checkbox-field",
            type: "checkbox",
            key: "generalPermission.editDocData",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.editDocData.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.editDocData.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.temporaries",
            defaultValue: false,
            hideExpression:
              this.displayedRole.role.type === "admin" ||
              this.displayedRole.role.type === "operator" ||
              this.displayedRole.role.type === "tagger",
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.temporaries.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.temporaries.placeholder"
              ),
            },
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.users",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.users.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.users.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.email",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.email.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.email.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            className: "col-4 checkbox-field",
            type: "checkbox",
            key: "generalPermission.alertLastDocs",
            defaultValue: false,
            templateOptions: {
              label: this.translateService.instant(
                "users.fields.generalPermission.alertLastDocs.label"
              ),
              placeholder: this.translateService.instant(
                "users.fields.generalPermission.alertLastDocs.placeholder"
              ),
            },
            hideExpression: this.displayedRole.role.type === "tagger",
            expressionProperties: {
              "templateOptions.onClick": (
                model: any,
                formState: any,
                field?: FormlyFieldConfig
              ): void => {
                const key = field.key as string;
                const subKey = key.replace("generalPermission.", "");
                if (
                  this.previousPermissions[subKey] !=
                  model.generalPermission[subKey]
                ) {
                  this.previousPermissions[subKey] =
                    model.generalPermission[subKey];
                  this.managePermissionCheckbox(model, field, subKey);
                }
              },
            },
          },
          {
            key: "userCompanyId",
            defaultValue: this.displayedRole.company
              ? this.displayedRole.company._id
              : "",
          },
          ...this.subPropertiesFormly,
        ],
      },
    ];

    this.fieldsPassword = [
      {
        key: "newPassword",
        validators: {
          fieldMatch: {
            expression: (control) => {
              const value = control.value;

              return (
                value.confirmPassword === value.password ||
                // avoid displaying the message error when values are empty
                !value.confirmPassword ||
                !value.password
              );
            },
            message: this.translateService.instant(
              "dialogs.changePassword.password_not_matching"
            ),
            errorPath: "confirmPassword",
          },
        },
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "password",
            templateOptions: {
              label: this.translateService.instant(
                "dialogs.changePassword.password"
              ),
              placeholder: this.translateService.instant(
                "dialogs.changePassword.password"
              ),
              required: true,
              minLength: 5,
              maxLength: 100,
              type: "password",
            },
          },
          {
            className: "col-12 col-md-6",
            type: "input",
            key: "confirmPassword",
            templateOptions: {
              label: this.translateService.instant(
                "dialogs.changePassword.confirmPassword"
              ),
              placeholder: this.translateService.instant(
                "dialogs.changePassword.confirmPassword"
              ),
              required: true,
              type: "password",
            },
          },
        ],
      },
    ];
  }

  /**
   * set/unset all permission checkbox on the unlimited permission change event
   */
  managePermissionCheckbox(model: any, field: any, subKey: string): void {
    const userType = this.displayedRole.type;
    if (subKey !== "unlimited") {
      if (
        ((userType === "admin" || userType === "operator") &&
          subKey !== "companies" &&
          subKey !== "temporaries") ||
        ((userType === "master" || userType === "collaborator") &&
          subKey !== "administration" &&
          subKey !== "editCompanies" &&
          subKey !== "insertPaperDoc")
      ) {
        this.previousPermissions[subKey] = model.generalPermission[subKey];

        let allChkTrue = true;
        for (const property in model.generalPermission) {
          if (property !== "unlimited") {
            allChkTrue = allChkTrue && model.generalPermission[property];
          }
        }
        if (allChkTrue) {
          this.previousPermissions.unlimited = false;
          model.generalPermission.unlimited = false;
          this.form.get("generalPermission.unlimited").setValue(true);
        } else {
          this.previousPermissions.unlimited = false;
          model.generalPermission.unlimited = false;
          this.form.get("generalPermission.unlimited").setValue(false);
        }
      }
    } else {
      for (const property in model.generalPermission) {
        if (
          typeof this.form.value.generalPermission[property] !== "undefined"
        ) {
          this.form
            .get("generalPermission." + property)
            .setValue(model.generalPermission.unlimited);
          this.previousPermissions[property] =
            model.generalPermission.unlimited;
        }
      }
    }
  }
  /**
   * set/unset all permission checkbox on the unlimited permission change event
   */
  managePropertiesPermissionCheckbox(
    model: any,
    field: any,
    subKey: string
  ): void {
    if (subKey !== "propertiesUnlimited") {
      if (!this.previousPermissions[subKey]) {
        this.previousPermissions.propertiesUnlimited = false;
        model.propsPermission.propertiesUnlimited = false;
        this.form.get("propsPermission.propertiesUnlimited").setValue(false);
      }
    } else {
      for (const property in model.subProperties) {
        if (typeof this.form.value.subProperties[property] !== "undefined") {
          this.form
            .get("subProperties." + property)
            .setValue(model.propsPermission.propertiesUnlimited);
        }
      }
    }
  }

  /**
   * Get company subProp data
   *
   * @since 1.0.0
   */
  getCompany(userId: string, selectedRole: any): void {
    this.subPropertiesFormly = [];
    if (!selectedRole) {
      this.toastrService.error(
        "Questo utente è da aggiornare con i nuovi ruoli"
      ); // @todo rimuovere quando non serve più
      return;
    }
    // Get company data
    if (
      selectedRole &&
      (selectedRole.role.type === "master" ||
        selectedRole.role.type === "collaborator")
    ) {
      this.companyId = selectedRole.company._id;
      // get all properties that user logged can manage
      this.companiesService.get(this.companyId).subscribe(
        (res) => {
          if (res.status) {
            let subProperties = res.data.subProperties;

            subProperties = subProperties
              .filter((el: any) => el.active)
              .sort((a: any, b: any) => {
                return this.compare(
                  a.name.toLowerCase(),
                  b.name.toLowerCase(),
                  true
                );
              });
            if (Array.isArray(subProperties) && subProperties) {
              const items: any[] = [];
              const item = {
                className: "section-label col-12",
                template:
                  "<hr><br /><div><strong>" +
                  this.translateService.instant(
                    "users.fields.companyPermission.title"
                  ) +
                  "</strong></div>",
              };
              items.push(item);
              const item2 = {
                className: "col-12 checkbox-field",
                type: "checkbox",
                key: "propsPermission.propertiesUnlimited",
                defaultValue: this.previousPermissions["propertiesUnlimited"],
                templateOptions: {
                  label: this.translateService.instant(
                    "users.fields.generalPermission.unlimited.label"
                  ),
                  placeholder: this.translateService.instant(
                    "users.fields.generalPermission.unlimited.placeholder"
                  ),
                },
                expressionProperties: {
                  "templateOptions.required":
                    "model.types && model.types.includes('manager')",
                  "templateOptions.onClick": (
                    model: any,
                    formState: any,
                    field?: FormlyFieldConfig
                  ): void => {
                    const key = field.key as string;
                    const subKey = key.replace("propsPermission.", "");
                    if (
                      this.previousPermissions[subKey] !=
                      model.propsPermission[subKey]
                    ) {
                      this.previousPermissions[subKey] =
                        model.propsPermission[subKey];
                      this.managePropertiesPermissionCheckbox(
                        model,
                        field,
                        subKey
                      );
                    }
                  },
                },
              };
              items.push(item2);
              subProperties.forEach((elem: any) => {
                if (elem.active) {
                  let defaultValue = true;
                  if (Array.isArray(elem.unauthUsers) && elem.unauthUsers) {
                    const index = elem.unauthUsers.findIndex((item: any) => {
                      return item === userId;
                    });
                    if (index > -1) {
                      defaultValue = false;
                    }
                  }
                  this.previousPermissions["subProperties." + elem.propertyId] =
                    defaultValue;
                  const item = {
                    className: "col-4 checkbox-field",
                    type: "checkbox",
                    key: "subProperties." + elem.propertyId,
                    defaultValue: defaultValue,
                    templateOptions: {
                      label: elem.name,
                    },
                    expressionProperties: {
                      "templateOptions.onClick": (
                        model: any,
                        formState: any,
                        field?: FormlyFieldConfig
                      ): void => {
                        const key = field.key as string;
                        const subKey = key.replace("subProperties.", "");
                        if (
                          this.previousPermissions[subKey] !=
                          model.subProperties[subKey]
                        ) {
                          this.previousPermissions[subKey] =
                            model.subProperties[subKey];
                          this.managePropertiesPermissionCheckbox(
                            model,
                            field,
                            subKey
                          );
                        }
                      },
                    },
                  };
                  items.push(item);
                }
              });
              this.subPropertiesFormly.push(...items);
            }
            this.initForm();
            return;
          }
        },
        (err) => {
          this.helperService.handleError(err);
        }
      );
    } else {
      this.initForm();
    }
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      console.warn(this.form);
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    // Remove blank values form model
    Object.keys(model).forEach((key: string) => {
      if (typeof model[key] === "string" && model[key].trim() === "") {
        model[key] = undefined;
      }
    });

    this.subscriptions.push(
      this.usersService
        .update(this.user.getId(), model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          (res) => {
            if (res) {
              this.toastrService.success(res.message);
              // Reload page
              this.handleRouteParamsChange();
              if (this.loggedUser._id === this.user.getId()) {
                this.authService.updatedLoggedUserData();
              }
            }
          },
          (err) => {
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Handle password form submit
   *
   * @since 1.0.0
   */
  onFormPasswordSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.formPassword.invalid) {
      Object.keys(this.formPassword.controls).forEach((key) => {
        this.formPassword.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      console.warn(this.formPassword);
      return;
    }

    // Activate submit button loading status
    this.formPasswordBtnOptions.active = true;

    const data = {
      password: model.newPassword.password,
    };

    this.subscriptions.push(
      this.usersService
        .updateFields(this.user.getId(), data)
        .pipe(finalize(() => (this.formPasswordBtnOptions.active = false)))
        .subscribe(
          (res) => {
            if (res) {
              this.toastrService.success(res.message);
              this.optionsPassword.resetModel();
              // Reload page
              this.handleRouteParamsChange();
              if (this.loggedUser._id === this.user.getId()) {
                this.authService.updatedLoggedUserData();
              }
            }
          },
          (err) => {
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Remove current user from database
   *
   * @since 1.0.0
   */
  onDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant("dialogs.confirm.title"),
        message: this.translateService.instant("dialogs.confirm.message"),
        btnOkText: this.translateService.instant("common.cta.confirm"),
        btnCancelText: this.translateService.instant("common.cta.undo"),
      },
      width: "500px",
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef
      .beforeClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) {
          return;
        }
        let paramCompanyId = undefined;
        if (
          typeof this.adminCompanyId !== "undefined" &&
          this.adminCompanyId !== ""
        ) {
          paramCompanyId = this.adminCompanyId;
        } else if (
          this.loggedUserObj.role.type === "master" || 
          this.loggedUserObj.role.type === "collaborator"
        ) {
          paramCompanyId = this.loggedUserObj.company._id;
        }
        this.subscriptions.push(
          this.usersService
            .delete(this.user.getId(), paramCompanyId)
            .pipe(finalize(() => (this.deleteBtnOptions.active = false)))
            .subscribe(
              (res2) => {
                this.toastrService.success(res2.message);
                if (this.adminCompanyId && this.adminCompanyId !== "") {
                  // Once the user is deleted, redirect to list
                  this.router.navigate([
                    this.lang,
                    "app",
                    "companies",
                    "detail",
                    this.adminCompanyId,
                  ]);
                } else {
                  // Once the user is deleted, redirect to list
                  this.router.navigate([this.lang, "app", "users", "list"]);
                }
                return;
              },
              (err) => {
                // Choose one of the following error handling
                // method. The first one shows a message right
                // under the form fields (if the form is properly
                // setted), the second one shows toastr
                // notifications for each error
                // this.helperService.handleFormError(this.passwordForm, err);
                this.helperService.handleError(err);
              }
            )
        );
      });
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean
  ): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}
